import { ValidationError } from "sequelize"
import {
    QidModel,
    QidModelCreate,
    QidModelSave,
    QidModelChangeStatus,
} from "server/models"
import { PRIOR_USE, PROVEN_IN_USE, PROVEN_NO, PUBLISHED, UNPUBLISHED } from "const"
import { OrderType, PublishedUnpublished } from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import {
    LangType,
    UpdateQidDependingDevices,
} from "client/store"
import {
    QidModelList,
    QidModelFilters,
    QidModelValidation,
} from "../types"


export const LoadAllQidModels = (productId: number, onlyActive = false) => (
    // TODO: order faiure modes and options by "order" column - not exists now
    GET(`/qidModel/productId/${productId}`)
    .then((models: QidModel[]) => {
        const modelList: QidModelList = {}
        models.forEach((model) => {
            if (onlyActive && model.status === UNPUBLISHED) return
            modelList[model.id] = model
        })
        return modelList
    })
)

export const LoadQidModelById = (modelId: number) => (
    GET(`/qidModel/id/${modelId}`)
    .then((model: QidModel) => model)
)

export const SaveQidModel = (
    id: number,
    model: QidModelCreate,
    updated: boolean,
    lang: LangType,
) => {
    const data: QidModelSave = {
        id,
        model,
    }
    return POST(
        "/qidModel/save",
        JSON.stringify(data),
    ).then((res) => {
        const newModel = res as QidModel
        const error = res as ValidationError
        const validation = <QidModelValidation>validate(error, lang)

        const result = {
            newModelId: newModel.id || null,
            validation,
        }

        if (!validation && id && updated) {
            // update SIL project devices
            return Promise.all([
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: model.productId,
                    qidModelId: id,
                }),
                UpdateQidDependingDevices({ // when model is deactivated it is affected on product level
                    update: true,
                    qidProductId: model.productId,
                    qidModelId: null,
                }),
            ]).then(() => result)
        }
        return result
    })
}

export const ChangeQidModelStatus = (model: QidModel) => {
    const status: PublishedUnpublished = model.status === PUBLISHED ? UNPUBLISHED : PUBLISHED
    const data: QidModelChangeStatus = {
        id: model.id,
        status,
    }
    return POST(
        "/qidModel/changeStatus",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
            ? status
            : model.status
    ))
}

export const DeleteQidModel = (modelId: number, productId: number) => (
    DELETE(`/qidModel/delete/${modelId}`)
    .then((removedRows: number) => {
        if (removedRows === 1) {
            // update SIL project devices
            return Promise.all([
                UpdateQidDependingDevices({
                    update: false,
                    qidProductId: productId,
                    qidModelId: modelId,
                }),
                UpdateQidDependingDevices({
                    update: true,
                    qidProductId: productId,
                    qidModelId: null,
                }),
            ]).then(() => removedRows)
        }
        return removedRows
    })
)


export const SortQidModels = (
    models: QidModel[],
    sortColumn: keyof QidModel,
    sortOrder: OrderType,
    filters: QidModelFilters,
): number[] => {
    const modelsFiltered = models.filter((model) => {
        let showRow = true
        if (filters.name && model.name.toUpperCase().indexOf(filters.name.toUpperCase()) < 0) showRow = false
        else if (filters.type && model.type !== filters.type) showRow = false

        else if (filters.lowDemandMode && model.lowDemandMode !== filters.lowDemandMode) showRow = false
        else if (filters.highDemandMode && model.highDemandMode !== filters.highDemandMode) showRow = false
        else if (filters.continuousMode && model.continuousMode !== filters.continuousMode) showRow = false

        else if (
            (filters.logicSolver === false || filters.logicSolver === true)
            && model.logicSolver !== filters.logicSolver
        ) showRow = false
        else if (
            [PROVEN_NO, PROVEN_IN_USE, PRIOR_USE].indexOf(filters.provenInUse) >= 0
            && model.provenInUse !== filters.provenInUse
        ) showRow = false

        else if (filters.status && model.status !== filters.status) showRow = false
        else if (filters.search) showRow = search(filters.search, model)
        return showRow
    })

    modelsFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))

    const modelIds: number[] = []
    modelsFiltered.forEach((product) => modelIds.push(product.id))

    return modelIds
}
